import React, { useState, useEffect, ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useBateaux, Bateau } from "@/hooks/useBateaux";
import { useModelesNavires } from "@/hooks/useModelesNavires";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Image, X } from "lucide-react";
import PortAutocomplete from "@/components/ports/PortAutocomplete";

interface FormulaireModificationBateauProps {
  bateau: Bateau;
  onClose: () => void;
}

const FormulaireModificationBateau = ({ bateau, onClose }: FormulaireModificationBateauProps) => {
  const { modifierBateau, uploadPhoto } = useBateaux();
  const { getConstructeurs, getModelesByConstructeur } = useModelesNavires();
  const [selectedConstructeur, setSelectedConstructeur] = useState<string>(bateau.nom_constructeur);
  const [imagePreview, setImagePreview] = useState<string | null>(bateau.photo_url);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showConstructeurInput, setShowConstructeurInput] = useState<boolean>(false);
  const [showModeleInput, setShowModeleInput] = useState<boolean>(false);

  const pavillons = [
    'France', 'Belgique', 'Suisse', 'Canada', 'Italie', 'Espagne', 
    'Portugal', 'Pays-Bas', 'Allemagne', 'Royaume-Uni', 'Monaco', 'Autre'
  ];

  const form = useForm<Omit<Bateau, 'id' | 'created_at'>>({
    defaultValues: {
      nom: bateau.nom,
      nom_constructeur: bateau.nom_constructeur,
      nom_modele: bateau.nom_modele,
      annee: bateau.annee,
      annee_construction: bateau.annee_construction || undefined,
      annee_achat: bateau.annee_achat || undefined,
      longueur: bateau.longueur,
      largeur: bateau.largeur,
      tirant_eau: bateau.tirant_eau || undefined,
      couleur: bateau.couleur || "",
      moteur: bateau.moteur,
      heures_moteur: bateau.heures_moteur || undefined,
      capacite_carburant: bateau.capacite_carburant,
      capacite_eau: bateau.capacite_eau,
      capacite_eaux_usees: bateau.capacite_eaux_usees || undefined,
      numero_hin: bateau.numero_hin || "",
      immatriculation: bateau.immatriculation || "",
      mmsi: bateau.mmsi || "",
      port_attache: bateau.port_attache || "",
      pavillon: bateau.pavillon || "",
      photo_url: bateau.photo_url || "",
      note: bateau.note || "",
    }
  });

  const handleConstructeurChange = (constructeur: string) => {
    if (constructeur === "✏️ Saisir un autre constructeur") {
      setShowConstructeurInput(true);
      form.setValue("nom_constructeur", "");
    } else {
      setShowConstructeurInput(false);
      setSelectedConstructeur(constructeur);
      form.setValue("nom_constructeur", constructeur);
    }
  };

  const handleModeleChange = (modele: string) => {
    if (modele === "✏️ Saisir un autre modèle") {
      setShowModeleInput(true);
      form.setValue("nom_modele", "");
    } else {
      setShowModeleInput(false);
      form.setValue("nom_modele", modele);
    }
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageFile(null);
    form.setValue("photo_url", "");
  };

  const onSubmit = async (data: Omit<Bateau, 'id' | 'created_at'>) => {
    let photoUrl = data.photo_url;
    
    // Upload de la nouvelle photo si présente
    if (imageFile) {
      const uploadResult = await uploadPhoto(imageFile, bateau.id);
      if (uploadResult.error) {
        toast({
          title: "Erreur",
          description: "Impossible de télécharger la photo.",
          variant: "destructive",
        });
        return;
      }
      photoUrl = uploadResult.data || "";
    }

    const bateauData = {
      ...data,
      photo_url: photoUrl,
    };

    const result = await modifierBateau(bateau.id, bateauData);
    if (!result.error) {
      onClose();
    }
  };

  const constructeurs = getConstructeurs();
  const modelesDisponibles = selectedConstructeur ? getModelesByConstructeur(selectedConstructeur) : [];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Photo du navire */}
        <div className="flex justify-center">
          <div className="relative w-full max-w-md">
            {imagePreview ? (
              <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src={imagePreview} 
                  alt="Aperçu du navire" 
                  className="w-full h-full object-cover"
                />
                <button 
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full p-1"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Image className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Cliquez pour télécharger</span> une photo
                  </p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  onChange={handleImageChange} 
                  accept="image/*"
                />
              </label>
            )}
          </div>
        </div>

        {/* Nom du navire */}
        <FormField
          control={form.control}
          name="nom"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom du navire *</FormLabel>
              <FormControl>
                <Input placeholder="Mon Bateau" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Constructeur et Modèle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="nom_constructeur"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nom du constructeur *</FormLabel>
                {showConstructeurInput ? (
                  <FormControl>
                    <Input 
                      placeholder="Saisir le nom du constructeur" 
                      {...field} 
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedConstructeur(e.target.value);
                      }}
                    />
                  </FormControl>
                ) : (
                  <Select onValueChange={handleConstructeurChange} value={selectedConstructeur}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un constructeur" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {constructeurs.map((constructeur) => (
                        <SelectItem key={constructeur} value={constructeur}>
                          {constructeur}
                        </SelectItem>
                      ))}
                      <SelectItem value="✏️ Saisir un autre constructeur">
                        ✏️ Saisir un autre constructeur
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nom_modele"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nom du modèle *</FormLabel>
                {showModeleInput ? (
                  <FormControl>
                    <Input placeholder="Saisir le nom du modèle" {...field} />
                  </FormControl>
                ) : (
                  <>
                    <Select onValueChange={handleModeleChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un modèle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {modelesDisponibles.map((modele) => (
                          <SelectItem key={modele.nom_modele} value={modele.nom_modele}>
                            {modele.nom_modele}
                          </SelectItem>
                        ))}
                        <SelectItem value="✏️ Saisir un autre modèle">
                          ✏️ Saisir un autre modèle
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Années */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="annee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Année *</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="annee_construction"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Année de construction</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="annee_achat"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Année d'achat</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="longueur"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Longueur (m) *</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="largeur"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Largeur (m) *</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tirant_eau"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tirant d'eau (m)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Couleur */}
        <FormField
          control={form.control}
          name="couleur"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Couleur du navire</FormLabel>
              <FormControl>
                <Input placeholder="Blanc, Bleu, Rouge..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Moteur et heures moteur */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="moteur"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Moteur *</FormLabel>
                <FormControl>
                  <Input placeholder="Volvo Penta D2-40" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="heures_moteur"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Heures moteur</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="1500"
                    {...field} 
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Capacités */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="capacite_carburant"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capacité carburant (L) *</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="capacite_eau"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capacité eau (L) *</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="capacite_eaux_usees"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capacité eaux usées (L)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Informations légales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="immatriculation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Immatriculation</FormLabel>
                <FormControl>
                  <Input placeholder="Ex: FR123456" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="numero_hin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Numéro HIN</FormLabel>
                <FormControl>
                  <Input placeholder="Hull Identification Number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="mmsi"
            render={({ field }) => (
              <FormItem>
                <FormLabel>MMSI</FormLabel>
                <FormControl>
                  <Input placeholder="Identifiant radio" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="port_attache"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Port d'attache</FormLabel>
                <FormControl>
                  <PortAutocomplete
                    placeholder="Tapez pour rechercher un port…"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="pavillon"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pavillon</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un pavillon" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {pavillons.map((pavillon) => (
                    <SelectItem key={pavillon} value={pavillon}>
                      {pavillon}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Note */}
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Notes personnelles sur le navire..."
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Boutons */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" className="bg-nautic hover:bg-nautic-light text-white">
            Modifier le bateau
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default FormulaireModificationBateau;
